/* Warnings the DM has not looked at yet.
 *
 * Every refusal in this app writes an amber line, and several times on 2026-09-29
 * one of them explained itself perfectly and went unread in the middle of a fight.
 * The clearest case: a player's End turn was refused because the name did not match,
 * the log said exactly that and what to do about it, and the DM saw a turn that did
 * not advance.
 *
 * The Activity button already counts everything. That is the wrong number — in a
 * busy round it is always large, so it carries no signal. This counts only what the
 * DM has not seen AND would want to act on.
 */
import type { ActivityEntry } from "./types";

/* Tones that mean something went wrong or was refused. "violet" and "green" are
   things happening correctly and are not worth interrupting anybody for. */
const WARNING_TONES = new Set(["amber", "red"]);

export const isWarning = (e: Pick<ActivityEntry, "tone">): boolean => WARNING_TONES.has(String(e.tone || ""));

/**
 * How many warnings arrived after the DM last opened the rail.
 *
 * `seenAt` is an ISO timestamp, and so is every entry's `time`, so this is a string
 * comparison rather than date parsing — ISO sorts lexically, and parsing 200 dates
 * on every render to answer "any new ones?" would be work for nothing.
 *
 * No `seenAt` means the DM has never opened it, and everything counts as unseen.
 */
export function unseenWarnings(activity: ActivityEntry[], seenAt?: string): number {
  let n = 0;
  for (const e of activity || []) {
    if (!isWarning(e)) continue;
    /* An entry with no time cannot be placed, and counting it forever would leave a
       badge nothing can clear. Treated as seen. */
    if (!e.time) continue;
    if (!seenAt || e.time > seenAt) n += 1;
  }
  return n;
}
